import { Controller } from "stimulus"

/**
 * Contact-only lookup for volunteer creation.
 * Fetches server-rendered HTML from VolunteerManagement::ContactSearchesController
 * for both search results and contact details (no inline HTML generation).
 *
 * Targets:
 *   - searchInput: Text input for searching
 *   - searchArea: Container for the search input (hidden when contact selected)
 *   - results: Dropdown container for search results (server-rendered)
 *   - selectedDisplay: Shows currently selected contact
 *   - contactName: Span showing the selected contact name
 *   - contactDetails: Container for contact methods (server-rendered)
 *   - contactId: Hidden field for contact_id
 *   - newContactFields: Container for inline new-contact fields (hidden by default)
 */
export default class extends Controller {
  static targets = [
    "searchInput", "searchArea", "results",
    "selectedDisplay", "contactName", "contactDetails",
    "contactId", "newContactFields"
  ]

  connect() {
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add("hidden")
    }
    this._preventEnterSubmit = (e) => {
      if (e.key === "Enter") { e.preventDefault() }
    }
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.addEventListener("keydown", this._preventEnterSubmit)
    }
  }

  disconnect() {
    clearTimeout(this.searchTimeout)
    if (this.hasSearchInputTarget && this._preventEnterSubmit) {
      this.searchInputTarget.removeEventListener("keydown", this._preventEnterSubmit)
    }
  }

  search(e) {
    if (e.key === "Enter") { return }
    if (e.key === "Escape") {
      this.resultsTarget.classList.add("hidden")
      return
    }

    clearTimeout(this.searchTimeout)
    const query = this.searchInputTarget.value.trim()

    if (query.length < 3) {
      this.resultsTarget.classList.add("hidden")
      return
    }

    this.searchTimeout = setTimeout(() => {
      const url = "/volunteer_management/contact_searches?search=" + encodeURIComponent(query)
      fetch(url)
        .then(response => response.text())
        .then(html => {
          // innerHTML used for trusted server-rendered content from Rails endpoint
          this.resultsTarget.innerHTML = html
          this.resultsTarget.classList.remove("hidden")
        })
        .catch(error => console.error("Contact search error:", error))
    }, 300)
  }

  selectContact(e) {
    const el = e.currentTarget
    const id = el.dataset.contactId
    const name = el.dataset.contactName

    this.contactIdTarget.value = id

    this.searchInputTarget.value = ""
    this.resultsTarget.classList.add("hidden")

    this.selectedDisplayTarget.classList.remove("hidden")
    this.contactNameTarget.textContent = name

    this.searchAreaTarget.classList.add("hidden")
    if (this.hasNewContactFieldsTarget) {
      this.newContactFieldsTarget.classList.add("hidden")
    }

    this.fetchContactDetails(id)
  }

  fetchContactDetails(contactId) {
    if (!this.hasContactDetailsTarget) return

    this.contactDetailsTarget.innerHTML = '<p class="text-xs text-gray-400">Loading...</p>'
    this.contactDetailsTarget.classList.remove("hidden")

    fetch("/volunteer_management/contact_searches/" + contactId)
      .then(response => {
        if (!response.ok) throw new Error("Contact not found")
        return response.text()
      })
      .then(html => {
        // innerHTML used for trusted server-rendered content from Rails endpoint
        this.contactDetailsTarget.innerHTML = html
        this.contactDetailsTarget.classList.remove("hidden")
      })
      .catch(() => {
        this.contactDetailsTarget.innerHTML = ""
        this.contactDetailsTarget.classList.add("hidden")
      })
  }

  showNewContactFields() {
    this.resultsTarget.classList.add("hidden")
    this.searchAreaTarget.classList.add("hidden")
    this.contactIdTarget.value = ""

    if (this.hasNewContactFieldsTarget) {
      this.newContactFieldsTarget.classList.remove("hidden")
    }
  }

  clear(e) {
    e.preventDefault()
    this.contactIdTarget.value = ""
    this.searchInputTarget.value = ""
    this.resultsTarget.classList.add("hidden")
    this.selectedDisplayTarget.classList.add("hidden")
    this.contactNameTarget.textContent = ""
    this.searchAreaTarget.classList.remove("hidden")

    if (this.hasContactDetailsTarget) {
      this.contactDetailsTarget.innerHTML = ""
      this.contactDetailsTarget.classList.add("hidden")
    }
    if (this.hasNewContactFieldsTarget) {
      this.newContactFieldsTarget.classList.add("hidden")
    }
  }

  clearNewContact(e) {
    e.preventDefault()
    if (this.hasNewContactFieldsTarget) {
      this.newContactFieldsTarget.classList.add("hidden")
    }
    this.searchAreaTarget.classList.remove("hidden")
  }

  closeResults(e) {
    if (this.hasResultsTarget && !this.element.contains(e.target)) {
      this.resultsTarget.classList.add("hidden")
    }
  }
}
